import { useMemo, useState, useEffect, useCallback } from 'react'
import { Graph } from 'Common/Graph/Graph'
import { format } from 'date-fns'

export type CapacityGraphProps = {
  xAxisData: string[]
  promisedCapacity: number[]
  underCapacity: number[]
  overCapacity: number[]
  equalCapacity: number[]
  handleWeekChange?: (weekIndex: number) => void
}

const hovertemplate = () => {
  return `%{text}<br>` + `<extra></extra>`
}

export const CapacityGraph = ({
  xAxisData,
  promisedCapacity,
  underCapacity,
  overCapacity,
  equalCapacity,
  handleWeekChange,
}: CapacityGraphProps) => {
  const colors = {
    underCapcityBarColor: '#D8D8D8',
    overCapacityBarColor: '#E87722',
    equalCapacityBarColor: '#838DC8',
    scatterLineColor: '#838DC8',
    selectedBarColor: '#003A40',
    barLineColor: 'transparent',
  }
  const PROMISED_CAPACITY_TRACE_NAME = 'Promised capacity'
  const UNDER_CAPACITY_TRACE_NAME = 'Under capacity week'
  const OVER_CAPACITY_TRACE_NAME = 'Over capacity week'
  const EQUAL_CAPACITY_TRACE_NAME = 'Matched capacity week'

  const transparentBorderColors = new Array(xAxisData?.length).fill(
    colors.barLineColor
  )

  const weeksFormatted = useMemo(() => {
    return xAxisData?.map((dateText: string, index: number) => {
      return `${format(new Date(dateText), 'dd MMM yyy')} (Week ${index + 1})`
    })
  }, [xAxisData])

  const renderHoverText = useCallback(
    (label: string, data: number[]) => {
      return data.map(
        (d: number, i: number) =>
          `<b>${label}</b><br>` +
          `<b>Week:  </b>${weeksFormatted[i]}<br>` +
          `<b>Total hours: </b>${d.toFixed(1)}<br> ` +
          `<b>Capacity:  </b>${promisedCapacity[i]}<br><br> `
      )
    },
    [xAxisData]
  )

  const promisedCapacityTrace = useMemo(() => {
    return {
      x: xAxisData,
      y: promisedCapacity,
      mode: 'lines',
      type: 'scatter',
      text: renderHoverText('Promised capacity', promisedCapacity),
      hovertemplate: hovertemplate(),
      name: PROMISED_CAPACITY_TRACE_NAME,
      line: {
        color: colors.scatterLineColor,
        width: 6,
      },
    }
  }, [promisedCapacity, xAxisData, colors.scatterLineColor])

  const traceOne = useMemo(() => {
    return {
      x: xAxisData,
      y: underCapacity,
      text: renderHoverText(UNDER_CAPACITY_TRACE_NAME, underCapacity),
      hovertemplate: hovertemplate(),
      textfont: { color: 'transparent' },
      marker: {
        color: colors.underCapcityBarColor,
        line: {
          color: transparentBorderColors,
          width: 6,
        },
      },
      name: UNDER_CAPACITY_TRACE_NAME,
      type: 'bar',
    }
  }, [
    underCapacity,
    colors.underCapcityBarColor,
    promisedCapacity,
    xAxisData,
    hovertemplate,
    transparentBorderColors,
  ])

  const traceTwo = useMemo(() => {
    return {
      x: xAxisData,
      y: overCapacity,
      textfont: { color: 'transparent' },
      text: renderHoverText(OVER_CAPACITY_TRACE_NAME, overCapacity),
      hovertemplate: hovertemplate(),
      marker: {
        color: colors.overCapacityBarColor,
        line: {
          color: transparentBorderColors,
          width: 6,
        },
      },
      name: OVER_CAPACITY_TRACE_NAME,
      type: 'bar',
    }
  }, [
    overCapacity,
    colors.overCapacityBarColor,
    promisedCapacity,
    xAxisData,
    hovertemplate,
    transparentBorderColors,
  ])

  const traceEqualCapacity = useMemo(() => {
    return {
      x: xAxisData,
      y: equalCapacity,
      text: renderHoverText(EQUAL_CAPACITY_TRACE_NAME, equalCapacity),
      hovertemplate: hovertemplate(),
      textfont: { color: 'transparent' },
      marker: {
        color: colors.equalCapacityBarColor,
        line: {
          color: transparentBorderColors,
          width: 6,
        },
      },
      name: EQUAL_CAPACITY_TRACE_NAME,
      type: 'bar',
    }
  }, [
    equalCapacity,
    colors.equalCapacityBarColor,
    promisedCapacity,
    xAxisData,
    hovertemplate,
    transparentBorderColors,
  ])

  const [underCapacityTrace, setUnderCapacityTrace] = useState(traceOne)
  const [overCapacityTrace, setOverCapacityTrace] = useState(traceTwo)
  const data = useMemo(
    () => [
      promisedCapacityTrace,
      underCapacityTrace,
      overCapacityTrace,
      traceEqualCapacity,
    ],
    [
      promisedCapacityTrace,
      underCapacityTrace,
      overCapacityTrace,
      traceEqualCapacity,
    ]
  )

  const getUpdatedBarOutlineColor = (
    selectedIndex: number,
    trace: typeof underCapacityTrace
  ) => {
    return trace.marker.line.color.map((color, index) =>
      index === selectedIndex ? colors.selectedBarColor : colors.barLineColor
    )
  }

  const updateSelectedTraceBorderColor = (
    trace: typeof underCapacityTrace,
    color: string[],
    setState: typeof setUnderCapacityTrace
  ) => {
    setState({
      ...trace,
      marker: {
        ...trace.marker,
        line: {
          ...trace.marker.line,
          color: color,
        },
      },
    })
  }

  const resetToDefaultTraceState = (
    trace: typeof underCapacityTrace,
    setState: typeof setUnderCapacityTrace
  ) => {
    setState({
      ...trace,
      marker: {
        ...trace.marker,
        line: {
          ...trace.marker.line,
          color: transparentBorderColors,
        },
      },
    })
  }

  const handleClick = (event: Readonly<Plotly.PlotMouseEvent>) => {
    const traceName = event.points[0].data.name
    const selectedIndex = xAxisData.findIndex(
      (data) => data === event.points[0].x
    )
    handleWeekChange && handleWeekChange(selectedIndex)
    if (traceName === UNDER_CAPACITY_TRACE_NAME) {
      const newColor = getUpdatedBarOutlineColor(
        selectedIndex,
        underCapacityTrace
      )
      updateSelectedTraceBorderColor(
        underCapacityTrace,
        newColor,
        setUnderCapacityTrace
      )
      resetToDefaultTraceState(overCapacityTrace, setOverCapacityTrace)
    } else if (traceName === OVER_CAPACITY_TRACE_NAME) {
      const newColor = getUpdatedBarOutlineColor(
        selectedIndex,
        overCapacityTrace
      )
      updateSelectedTraceBorderColor(
        overCapacityTrace,
        newColor,
        setOverCapacityTrace
      )
      resetToDefaultTraceState(underCapacityTrace, setUnderCapacityTrace)
    }
  }

  useEffect(() => {
    const firstOverCapacityIndex = overCapacity.findIndex((data) => data !== 0)
    const firstUnderCapacityIndex = underCapacity.findIndex(
      (data) => data !== 0
    )
    const newColorOverCapacity = getUpdatedBarOutlineColor(
      firstOverCapacityIndex,
      overCapacityTrace
    )
    const newColorUnderCapacity = getUpdatedBarOutlineColor(
      firstUnderCapacityIndex,
      underCapacityTrace
    )
    if (firstOverCapacityIndex !== -1) {
      updateSelectedTraceBorderColor(
        overCapacityTrace,
        newColorOverCapacity,
        setOverCapacityTrace
      )
    }
    if (firstUnderCapacityIndex !== -1) {
      updateSelectedTraceBorderColor(
        underCapacityTrace,
        newColorUnderCapacity,
        setUnderCapacityTrace
      )
    }
  }, [])

  const layout = {
    barmode: 'stack',
    height: 300,
    autosize: true,
    yaxis: {
      showgrid: true,
    },
    xaxis: {
      tickformat: '%d %b %y',
      zeroline: true,
      nticks: 10,
    },
    legend: { orientation: 'h', x: 0.3, y: -0.3 },
  }

  return <Graph data={data} layout={layout} onClick={handleClick} />
}
